import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

const PersonalInfo = ({ profile }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isIt = i18n.language?.startsWith('it');
  const isEs = i18n.language?.startsWith('es');

  /* ── reusable pieces ─────────────────────────── */
  const SectionLabel = ({ children }) => (
    <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--c-texSec, #7d7a75)', marginBottom: 8, fontWeight: 600 }}>
      {children}
    </div>
  );

  const Card = ({ children }) => (
    <div style={{ background: 'var(--c-bg-secondary, #F5F0EB)', borderRadius: 16, padding: '4px 16px', marginBottom: 20 }}>
      {children}
    </div>
  );

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', fontFamily: 'Manrope-Variable' }}>
      <span style={{ fontSize: 14, color: 'var(--c-texSec, #7d7a75)' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--c-texPri, #191919)', textAlign: 'right', maxWidth: '55%' }}>{value || '–'}</span>
    </div>
  );

  /* ── format helpers ──────────────────────────── */
  const fmtMotivation = (v) => {
    if (!v) return '–';
    const m = { improve_relationship: isIt ? 'Migliorare la relazione' : isEs ? 'Mejorar la relación' : 'Improve relationship', be_present: isIt ? 'Essere più presente' : isEs ? 'Estar más presente' : 'Be more present', do_more: isIt ? 'Fare di più' : isEs ? 'Hacer más' : 'Do more', reconnect: isIt ? 'Riconnettersi' : isEs ? 'Reconectar' : 'Reconnect' };
    return m[v] || v;
  };
  const fmtRelationship = (v) => {
    if (!v) return '–';
    const m = { married: isIt ? 'Sposati' : isEs ? 'Casados' : 'Married', living_together: isIt ? 'Conviventi' : isEs ? 'Conviviendo' : 'Living together', in_relationship: isIt ? 'In una relazione' : isEs ? 'En una relación' : 'In a relationship' };
    return m[v] || v;
  };
  const fmtWork = (v) => {
    if (!v) return '–';
    const m = { freelance: 'Freelance', part_time: 'Part-time', full_time: 'Full-time', entrepreneur: isIt ? 'Imprenditore' : isEs ? 'Emprendedor' : 'Entrepreneur', other: isIt ? 'Altro' : isEs ? 'Otro' : 'Other' };
    return m[v] || v;
  };
  const fmtAreas = (a) => {
    if (!a || !Array.isArray(a) || !a.length) return '–';
    const m = { quality_time: isIt ? 'Tempo di qualità' : isEs ? 'Tiempo de calidad' : 'Quality time', home_management: isIt ? 'Gestione casa' : isEs ? 'Gestión del hogar' : 'Home management', emotional_presence: isIt ? 'Presenza emotiva' : isEs ? 'Presencia emocional' : 'Emotional presence', reduce_tension: isIt ? 'Ridurre tensione' : isEs ? 'Reducir tensión' : 'Reduce tension', confidence: isIt ? 'Sicurezza' : isEs ? 'Confianza' : 'Confidence' };
    return a.map(x => m[x] || x).join(', ');
  };
  const fmtTime = (v) => {
    if (!v) return '–';
    if (v === -1 || v === '-1') return isIt ? 'Dipende dal giorno' : isEs ? 'Depende del día' : 'Depends on the day';
    return `${v} ${isIt ? 'minuti' : isEs ? 'minutos' : 'min'}`;
  };

  /* ── render ──────────────────────────────────── */
  return (
    <div style={{ background: 'inherit', fontFamily: 'Manrope-Variable' }}>
      <div style={{ maxWidth: 420, margin: '0 auto', padding: '0 24px', paddingBottom: 100 }}>

        {/* Back */}
        <div style={{ padding: '24px 0 12px' }}>
          <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <Icon icon="solar:alt-arrow-left-linear" style={{ width: 16, height: 16, color: 'var(--c-texPri, #191919)' }} />
            <span style={{ fontSize: 16, color: 'var(--c-texPri, #191919)' }}>{isIt ? 'Indietro' : isEs ? 'Atrás' : 'Back'}</span>
          </button>
        </div>

        {/* Personal information */}
        <div style={{ paddingTop: 8 }}>
          <SectionLabel>{isIt ? 'Informazioni personali' : isEs ? 'Información personal' : 'Personal information'}</SectionLabel>
          <Card>
            <Row label={isIt ? 'Nome' : isEs ? 'Nombre' : 'Name'} value={profile?.name} />
            <Row label="Email" value={profile?.email} />
            <Row label={isIt ? 'Data di nascita' : isEs ? 'Fecha de nacimiento' : 'Birth date'} value={profile?.birth_date ? new Date(profile.birth_date).toLocaleDateString(isIt ? 'it-IT' : isEs ? 'es-ES' : 'en-US') : null} />
            <Row label={isIt ? 'Fuso orario' : isEs ? 'Zona horaria' : 'Timezone'} value={profile?.timezone} />
          </Card>
        </div>

        {/* About you */}
        <div>
          <SectionLabel>{isIt ? 'Su di te' : isEs ? 'Sobre ti' : 'About you'}</SectionLabel>
          <Card>
            <Row label={isIt ? 'Relazione' : isEs ? 'Relación' : 'Relationship'} value={fmtRelationship(profile?.relationship_type)} />
            <Row label={isIt ? 'Età' : isEs ? 'Edad' : 'Age'} value={profile?.age_cluster || '–'} />
            <Row label={isIt ? 'Motivazione' : isEs ? 'Motivación' : 'Motivation'} value={fmtMotivation(profile?.motivation)} />
            <Row label={isIt ? 'Lavoro' : isEs ? 'Trabajo' : 'Work'} value={fmtWork(profile?.work_schedule)} />
          </Card>
        </div>

        {/* Preferences */}
        <div>
          <SectionLabel>{isIt ? 'Le tue preferenze' : isEs ? 'Tus preferencias' : 'Your preferences'}</SectionLabel>
          <Card>
            <Row label={isIt ? 'Tempo disponibile' : isEs ? 'Tiempo disponible' : 'Available time'} value={fmtTime(profile?.time_budget_minutes)} />
            <Row label={isIt ? 'Aree di miglioramento' : isEs ? 'Áreas de mejora' : 'Improvement areas'} value={fmtAreas(profile?.improvement_areas)} />
          </Card>
        </div>

        {/* Edit */}
        <div
          onClick={() => navigate('/profile/edit')}
          style={{ background: 'var(--c-bg-secondary, #F5F0EB)', borderRadius: 16, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--c-texPri, #191919)' }}>{isIt ? 'Modifica profilo' : isEs ? 'Editar perfil' : 'Edit profile'}</div>
            <div style={{ fontSize: 11, color: 'var(--c-texSec, #7d7a75)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{isIt ? 'Aggiorna i tuoi dati' : isEs ? 'Actualiza tus datos' : 'Update your data'}</div>
          </div>
          <Icon icon="solar:alt-arrow-right-linear" style={{ width: 14, height: 14, color: 'var(--c-texSec, #7d7a75)', opacity: 0.5 }} />
        </div>
      </div>
    </div>
  );
};

export default PersonalInfo;